import {
  format,
  formatDistance,
  formatDistanceToNow,
  formatRelative,
  isToday,
  isYesterday,
  isThisWeek,
  isThisYear,
  parseISO,
  isValid,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';

/**
 * Date utility functions using date-fns
 * Centralized date handling for the entire marketplace application
 */

// Parse date safely - handles both ISO strings and Date objects
export const parseDate = (dateInput) => {
  if (!dateInput) return null;
  
  if (dateInput instanceof Date) {
    return isValid(dateInput) ? dateInput : null;
  }
  
  if (typeof dateInput === 'string') {
    const parsed = parseISO(dateInput);
    return isValid(parsed) ? parsed : null;
  }
  
  // Handle Firestore timestamps
  if (dateInput && typeof dateInput === 'object' && dateInput._seconds) {
    return new Date(dateInput._seconds * 1000);
  }
  
  return null;
};

// Format date for display
export const formatDate = (dateInput, formatString = 'MMM dd, yyyy') => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

// Format time for display
export const formatTime = (dateInput, formatString = 'h:mm a') => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid time';
  
  try {
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

// Format date and time together
export const formatDateTime = (dateInput, formatString = 'MMM dd, yyyy h:mm a') => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date time:', error);
    return 'Invalid date';
  }
};

// Smart relative date formatting (e.g., "2 hours ago", "yesterday", "3 days ago")
export const formatRelativeDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    if (isToday(date)) {
      return 'Today';
    }
    
    if (isYesterday(date)) {
      return 'Yesterday';
    }
    
    if (isThisWeek(date)) {
      return formatRelative(date, new Date());
    }
    
    if (isThisYear(date)) {
      return format(date, 'MMM dd');
    }
    
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return formatDate(date);
  }
};

// Format distance to now (e.g., "2 hours ago", "3 days ago")
export const formatDistanceFromNow = (dateInput, options = {}) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return formatDistanceToNow(date, { addSuffix: true, ...options });
  } catch (error) {
    console.error('Error formatting distance to now:', error);
    return 'Invalid date';
  }
};

// Format timestamp for chat/messages
export const formatTimestamp = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    const now = new Date();
    
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }
    
    if (isYesterday(date)) {
      return 'Yesterday';
    }
    
    if (isThisWeek(date)) {
      return format(date, 'EEE');
    }
    
    if (isThisYear(date)) {
      return format(date, 'MMM dd');
    }
    
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

// Format order date
export const formatOrderDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting order date:', error);
    return 'Invalid date';
  }
};

// Format order time
export const formatOrderTime = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid time';
  
  try {
    return format(date, 'h:mm a');
  } catch (error) {
    console.error('Error formatting order time:', error);
    return 'Invalid time';
  }
};

// Format review date
export const formatReviewDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting review date:', error);
    return 'Invalid date';
  }
};

// Format document upload date
export const formatUploadDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting upload date:', error);
    return 'Invalid date';
  }
};

// Format business hours date
export const formatBusinessDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return 'Invalid date';
  
  try {
    return format(date, 'MMM dd');
  } catch (error) {
    console.error('Error formatting business date:', error);
    return 'Invalid date';
  }
};

// Get current date as ISO string
export const getCurrentDateISO = () => {
  return new Date().toISOString();
};

// Get current timestamp
export const getCurrentTimestamp = () => {
  return Date.now();
};

// Check if date is in the past
export const isPast = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return false;
  
  return date < new Date();
};

// Check if date is in the future
export const isFuture = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return false;
  
  return date > new Date();
};

// Get days difference between two dates
export const getDaysDifference = (date1, date2) => {
  const parsedDate1 = parseDate(date1);
  const parsedDate2 = parseDate(date2);
  
  if (!parsedDate1 || !parsedDate2) return 0;
  
  return differenceInDays(parsedDate2, parsedDate1);
};

// Get hours difference between two dates
export const getHoursDifference = (date1, date2) => {
  const parsedDate1 = parseDate(date1);
  const parsedDate2 = parseDate(date2);
  
  if (!parsedDate1 || !parsedDate2) return 0;
  
  return differenceInHours(parsedDate2, parsedDate1);
};

// Get minutes difference between two dates
export const getMinutesDifference = (date1, date2) => {
  const parsedDate1 = parseDate(date1);
  const parsedDate2 = parseDate(date2);
  
  if (!parsedDate1 || !parsedDate2) return 0;
  
  return differenceInMinutes(parsedDate2, parsedDate1);
};

// Add days to date
export const addDaysToDate = (dateInput, days) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return addDays(date, days);
  } catch (error) {
    console.error('Error adding days to date:', error);
    return null;
  }
};

// Subtract days from date
export const subtractDaysFromDate = (dateInput, days) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return subDays(date, days);
  } catch (error) {
    console.error('Error subtracting days from date:', error);
    return null;
  }
};

// Get start of day
export const getStartOfDay = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return startOfDay(date);
  } catch (error) {
    console.error('Error getting start of day:', error);
    return null;
  }
};

// Get end of day
export const getEndOfDay = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return endOfDay(date);
  } catch (error) {
    console.error('Error getting end of day:', error);
    return null;
  }
};

// Get start of week
export const getStartOfWeek = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return startOfWeek(date, { weekStartsOn: 1 }); // Monday start
  } catch (error) {
    console.error('Error getting start of week:', error);
    return null;
  }
};

// Get end of week
export const getEndOfWeek = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return endOfWeek(date, { weekStartsOn: 1 }); // Monday start
  } catch (error) {
    console.error('Error getting end of week:', error);
    return null;
  }
};

// Get start of month
export const getStartOfMonth = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return startOfMonth(date);
  } catch (error) {
    console.error('Error getting start of month:', error);
    return null;
  }
};

// Get end of month
export const getEndOfMonth = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return null;
  
  try {
    return endOfMonth(date);
  } catch (error) {
    console.error('Error getting end of month:', error);
    return null;
  }
};

// Format date for legal pages (last updated)
export const formatLegalDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date) return new Date().toLocaleDateString(); // Fallback
  
  try {
    return format(date, 'MMMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting legal date:', error);
    return new Date().toLocaleDateString(); // Fallback
  }
};

// Export default for convenience
export default {
  parseDate,
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeDate,
  formatDistanceFromNow,
  formatTimestamp,
  formatOrderDate,
  formatOrderTime,
  formatReviewDate,
  formatUploadDate,
  formatBusinessDate,
  getCurrentDateISO,
  getCurrentTimestamp,
  isPast,
  isFuture,
  getDaysDifference,
  getHoursDifference,
  getMinutesDifference,
  addDaysToDate,
  subtractDaysFromDate,
  getStartOfDay,
  getEndOfDay,
  getStartOfWeek,
  getEndOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  formatLegalDate,
};
